import { ChangeEvent, use, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PickersInputComponentLocaleText } from '@mui/x-date-pickers';
import { Moment } from 'moment';
import { validateContinuityOfTimeFrames, validateTimeFrames } from 'shared/utils/timeframe-utils';

import TimeFrame from 'features/residents/models/configurations/time-frame';
import WanderingDetectionConfiguration from 'features/residents/models/configurations/wandering-detection-configuration';
import WanderingDetectionTimeFrameConfiguration from 'features/residents/models/configurations/wandering-detection-time-frame-configuration';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type Props = {
  wanderingDetectionConfiguration: WanderingDetectionConfiguration;
  activeAllDay: boolean;
  valuesChanged: (newTimeFrameConfigurations: WanderingDetectionTimeFrameConfiguration[] | null, newActiveAllDay: boolean) => void;
  index: number;
};

export const useWanderingDetectionTimeFrames = ({ wanderingDetectionConfiguration, activeAllDay, valuesChanged, index }: Props) => {
  const { t } = useTranslation('residentsSettings');

  const [validationErrors, setValidationErrors] = useState<boolean[]>([]);
  const [shouldShowDiscontinuityAlert, setShouldShowDiscontinuityAlert] = useState<boolean>(false);

  const {
    detailEditState: { viewingMode, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);
  const { formState } = useFormContext();

  const defaultTimeFrame: TimeFrame = {
    from: '00:00:00.000',
    to: '00:00:00.000',
  };
  const fullDayTimeFrame: TimeFrame = {
    from: '00:00:00.000',
    to: '23:59:59.999',
  };

  const addTimeFrameConfiguration = () => {
    const newTimeFrameConfigurations = [...(wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations ?? [])];
    newTimeFrameConfigurations.push({
      wanderingBoundaryLocationIds: [],
      timeFrame: defaultTimeFrame,
    });

    valuesChanged(newTimeFrameConfigurations, activeAllDay);
  };

  const removeTimeFrameConfiguration = (frameIndex: number) => {
    const newTimeFrameConfigurations = [...(wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations ?? [])];
    newTimeFrameConfigurations.splice(frameIndex, 1);

    valuesChanged(newTimeFrameConfigurations, activeAllDay);
  };

  const onTimeFrameChanged = (newValue: Moment | null, frameIndex: number, isFromField: boolean) => {
    const newTimeFrameConfigurations = [...(wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations ?? [])];
    const formattedValue = newValue?.format('HH:mm:ss') ?? '';
    if (isFromField) {
      newTimeFrameConfigurations[frameIndex].timeFrame.from = formattedValue;
    } else {
      newTimeFrameConfigurations[frameIndex].timeFrame.to = formattedValue;
    }

    valuesChanged(newTimeFrameConfigurations, activeAllDay);
  };

  const validate = (newTimeFrameConfigurations: WanderingDetectionTimeFrameConfiguration[]) => {
    const newValidationErrors = validateTimeFrames(newTimeFrameConfigurations.map((x) => x.timeFrame));
    setValidationErrors(newValidationErrors);

    if (newValidationErrors.every((x) => !x)) {
      residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    } else {
      residentSettingEditForm.setError(`organisationUnitScenarios.${index}`, { type: 'pattern' });
    }
  };

  const setAllDayTimeFrame = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const allDayTimeFrame = {
        wanderingBoundaryLocationIds: [],
        timeFrame: fullDayTimeFrame,
      };
      valuesChanged([allDayTimeFrame], checked);
      setShouldShowDiscontinuityAlert(false);
    } else {
      const newTimeFrameConfigurations = [...(wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations ?? [])];
      if (newTimeFrameConfigurations.length === 0) {
        newTimeFrameConfigurations.push({
          wanderingBoundaryLocationIds: [],
          timeFrame: defaultTimeFrame,
        });
      } else if (
        newTimeFrameConfigurations.length === 1 &&
        newTimeFrameConfigurations[0].timeFrame.from === fullDayTimeFrame.from &&
        newTimeFrameConfigurations[0].timeFrame.to === fullDayTimeFrame.to
      ) {
        newTimeFrameConfigurations[0] = {
          ...newTimeFrameConfigurations[0],
          timeFrame: defaultTimeFrame,
          wanderingBoundaryLocationIds: [],
        };
      }
      valuesChanged(newTimeFrameConfigurations, checked);
    }
  };

  const timePickerLocaleText: PickersInputComponentLocaleText = {
    fieldHoursPlaceholder: () => t('timePickerLocale.fieldHoursPlaceholder'),
    fieldMinutesPlaceholder: () => t('timePickerLocale.fieldMinutesPlaceholder'),
  };

  useEffect(() => {
    if (formState.isSubmitting && wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations != null) {
      validate(wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations);
    }
  }, [formState.isSubmitting, wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations]);

  useEffect(() => {
    if (activeAllDay) {
      setShouldShowDiscontinuityAlert(false);
      return;
    }

    const timeFrames = wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations?.map((x) => x.timeFrame) ?? [];
    if (validateTimeFrames(timeFrames).some(Boolean)) {
      setShouldShowDiscontinuityAlert(false);
      return;
    }

    setShouldShowDiscontinuityAlert(!validateContinuityOfTimeFrames(timeFrames));
  }, [wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations]);

  useEffect(() => {
    setValidationErrors([]);
  }, [viewingMode]);

  return {
    viewingMode,
    validationErrors,
    timePickerLocaleText,
    addTimeFrameConfiguration,
    removeTimeFrameConfiguration,
    onTimeFrameChanged,
    setAllDayTimeFrame,
    shouldShowDiscontinuityAlert,
  };
};
